class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    document.addEventListener('cart:refresh', this.refreshFromServer.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, open = true) {
    this.classList.toggle('is-empty', Number(parsedState.item_count) === 0);
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const sectionKey = section.section || section.id;
      const sectionHtml = parsedState.sections && parsedState.sections[sectionKey];

      if (!sectionElement || !sectionHtml) return;

      const replacementHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (replacementHtml) sectionElement.innerHTML = replacementHtml;
    });

    if (open) {
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      });
    }
  }

  refreshFromServer() {
    this.showAddedToast();
    window.dispatchEvent(
      new CustomEvent('parently:cart-added', {
        detail: { quantity: 1 },
      })
    );

    Promise.all([
      fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`Cart state refresh failed: ${response.status}`);
        return response.json();
      }),
      fetch(`${routes.cart_url}?section_id=cart-drawer`, { headers: { Accept: 'text/html' } }).then((response) => {
        if (!response.ok) throw new Error(`Cart drawer refresh failed: ${response.status}`);
        return response.text();
      }),
    ])
      .then(([cartState, drawerHtml]) => {
        this.updateCartIcon(cartState.item_count);

        const sourceDrawer = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
        const targetDrawer = this.querySelector('#CartDrawer');
        if (sourceDrawer && targetDrawer) {
          targetDrawer.innerHTML = sourceDrawer.innerHTML;
          this.classList.toggle('is-empty', cartState.item_count === 0);
          this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        }
      })
      .catch((error) => console.error(error));
  }

  showAddedToast() {
    const toast = document.getElementById('parently-cart-added-toast');
    if (!toast) return;

    const message = toast.querySelector('[data-cart-toast-message]');
    if (message) message.textContent = 'Item added';
    toast.classList.add('is-visible');
    toast.setAttribute('aria-hidden', 'false');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.setAttribute('aria-hidden', 'true');
    }, 4500);
  }

  updateCartIcon(itemCount) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    const count = Number(itemCount) || 0;
    const existingBubble = cartIcon.querySelector('.cart-count-bubble');
    if (count === 0) {
      existingBubble?.remove();
      return;
    }

    const bubble = existingBubble || document.createElement('div');
    bubble.className = 'cart-count-bubble';
    bubble.innerHTML = '';

    const visibleCount = document.createElement('span');
    visibleCount.setAttribute('aria-hidden', 'true');
    if (count < 100) visibleCount.textContent = count;

    const accessibleCount = document.createElement('span');
    accessibleCount.className = 'visually-hidden';
    accessibleCount.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;

    bubble.append(visibleCount, accessibleCount);
    if (!existingBubble) cartIcon.appendChild(bubble);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
